import { Button } from "@/components/Button";
import { LayoutBody, LayoutHeader } from "@/components/Layout";
import { Spinner } from "@/components/Spinner";
import { BaseLivlyApiResponse } from "@/types/Base";
import { BASE_API_URL } from "@/utils/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export const getClickPayUrl = async (leaseId: number) => {
  const result = await axios.get<
    BaseLivlyApiResponse<{
      url: string;
      leaseId: number;
      leaseExternalId: string;
      token: string;
    }>
  >(`${BASE_API_URL}/livly/users/leases/${leaseId}/clickPayUrl`);

  return result.data.Data;
};

export default function ClickPayContainer({
  title,
  leaseId,
}: {
  title: string;
  leaseId?: number;
}) {
  const isFromNativeApp = localStorage.getItem("mobile") === "true";

  if (isFromNativeApp) {
    return <ClickPayWidgetMobile leaseId={leaseId} />;
  }

  return <ClickPayWidget leaseId={leaseId} title={title} />;
}

function ClickPayWidget({
  title,
  leaseId,
}: {
  title: string;
  leaseId?: number;
}) {
  const { isLoading, mutate } = useMutation(() => getClickPayUrl(leaseId!));
  useQuery(["click-pay", leaseId], () => getClickPayUrl(leaseId!), {
    onSuccess(data) {
      if (data.url) {
        window.open(data.url, "_blank");
      }
    },
  });

  const onClick = () => {
    mutate(undefined, {
      onSuccess: (data) => {
        if (data.url) {
          window.open(data.url, "_blank");
        }
      },
    });
  };

  return (
    <div className="flex flex-col h-screen">
      <LayoutHeader title={title} />
      <LayoutBody className="flex flex-col space-y-4 items-center justify-center flex-1">
        <p className="text-sm text-gray-700">
          Your content will open in a new tab. If it doesn't, please click the
          button below.
        </p>
        <Button
          color="secondary"
          onClick={onClick}
          disabled={isLoading}
          data-test="clickpay-button"
        >
          Pay Rent with ClickPay
        </Button>
      </LayoutBody>
    </div>
  );
}

function ClickPayWidgetMobile({ leaseId }: { leaseId?: number }) {
  const { isLoading, isFetching, isError } = useQuery(
    ["click-pay", leaseId],
    () => getClickPayUrl(leaseId!),
    {
      onSuccess(data) {
        if (data.url) {
          window.location.replace(data.url);
        }
      },
    }
  );

  return (
    <div className="flex flex-col h-screen">
      <LayoutBody className="flex items-center justify-center flex-1">
        {isError && <p>Error loading ClickPay</p>}
        {(isLoading || isFetching) && <Spinner color="livly" size="xl" />}
      </LayoutBody>
    </div>
  );
}
